import React, { createContext, useContext, useState, useEffect } from 'react';

// Define business data type
interface Business {
  id: string;
  name: string;
  status: string;
  userRole: string;
}

// Define the context type
interface BusinessContextType {
  business: Business | null;
  isLoading: boolean;
  error: string | null;
  refreshBusiness: () => Promise<void>;
}

// Create context with default undefined value
const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBusinessInfo = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/business', {
        method: 'GET',
        credentials: 'same-origin' // Include cookies for authentication
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setBusiness(data.business);
      } else {
        // This might not be an error case if the user simply doesn't have a business
        // Set business to null but don't treat it as an error
        setBusiness(null);
        if (response.status !== 404) { // Only set error for non-404 responses
          setError(data.error || 'Failed to fetch business information');
        }
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setBusiness(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchBusinessInfo();
  }, []);

  // Refresh function to manually trigger a refresh of business data
  const refreshBusiness = async () => {
    await fetchBusinessInfo();
  };

  return (
    <BusinessContext.Provider
      value={{
        business,
        isLoading,
        error,
        refreshBusiness
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};